import { createSelector } from '@reduxjs/toolkit';
import { groupBy } from 'lodash-es';
import {
  selectUserActiveEmployerBusinessId,
  selectUserActiveEmployerInfo,
} from 'store/user/selectors.js/user.selectors';
import { arrayToMap } from 'utils/arrayToMap';
import { getProjectsActivityFilterCacheKey } from '../utils/getProjectsActivityFilterCacheKey';
import {
  getSensorDisplayOfPreference,
  getUserProjectPreferenceKey,
  isProjectHiddenByPreferenceOrAncestor,
} from '../utils/userProjectPreferences';
import { selectShortTermProjectsSearchFilters } from './shortTermProjects.selectors';

const selectProjectsState = (state) => state.projects;

export const selectProjects = createSelector(
  [selectProjectsState],
  (state) => state.projects?.entities || {}
);

export const selectProjectsOfBusiness = createSelector(
  [selectProjectsState],
  (state) => state.projectsOfBusiness.entities
);

export const selectProjectsFolder = createSelector(
  [selectProjectsState],
  (state) => state.folder
);

export const selectSearchFilter = createSelector(
  [selectProjectsState],
  (state) => state.searchFilter
);

export const selectSearchFilterActive = createSelector(
  [selectProjectsState],
  (state) => (state.searchFilter ? true : false)
);

export const PROJECTS_ACTIVITY_FILTER_MODES = {
  LATEST: 'USER_ACTIVITY_LAST_30_DAYS',
  ALL: 'ALL_PROJECTS',
  VEROTTAJAILMOITETTAVA: 'VEROTTAJAILMOITETTAVA_PROJECTS',
};

// Sub-modes of the LATEST activity filter: whether "activity" means anything
// (records, viewed files, favorites, opened projects) or only own time records.
export const LATEST_PROJECTS_SOURCE_MODES = {
  ANY_ACTIVITY: 'ANY_ACTIVITY',
  RECORDS: 'RECORDS',
};

// Sub-modes of the VEROTTAJAILMOITETTAVA activity filter (the tax-filing
// view): the projects of all three sources, or only those of the unsent
// filings source, the ones with filings still to send. Not every yellow-dot
// (flagged reportable) row: the subcontractor invoices source can list a
// flagged project that has nothing to file.
export const VEROTTAJAILMOITETTAVA_LIST_MODES = {
  ALL: 'ALL',
  UNSENT_FILINGS: 'UNSENT_FILINGS',
};

// The unsent filings source's key in verottajailmoitettavaProjectIdsByEmployer
// and in its request statuses (VEROTTAJAILMOITETTAVA_SOURCE_KEYS of
// thunks/loadVerottajailmoitettavaProjectIdsBySource.thunks.js).
const UNSENT_FILINGS_SOURCE_KEY = 'unsentFilings';

export const selectProjectsActivityFilterMode = createSelector(
  [selectProjectsState],
  (state) =>
    state.projectsActivityFilterMode ||
    (state.showAll
      ? PROJECTS_ACTIVITY_FILTER_MODES.ALL
      : PROJECTS_ACTIVITY_FILTER_MODES.LATEST)
);

export const selectShowAll = createSelector(
  [selectProjectsActivityFilterMode],
  (projectsActivityFilterMode) =>
    projectsActivityFilterMode === PROJECTS_ACTIVITY_FILTER_MODES.ALL
);

export const selectLatestProjectsListSourceMode = createSelector(
  [selectProjectsState],
  (state) =>
    state.latestProjectsListSourceMode ||
    LATEST_PROJECTS_SOURCE_MODES.ANY_ACTIVITY
);

export const selectTimerDrawerSourceMode = createSelector(
  [selectProjectsState],
  (state) => state.timerDrawerSourceMode || LATEST_PROJECTS_SOURCE_MODES.RECORDS
);

export const selectVerottajailmoitettavaListMode = createSelector(
  [selectProjectsState],
  (state) =>
    state.verottajailmoitettavaListMode || VEROTTAJAILMOITETTAVA_LIST_MODES.ALL
);

export const selectShowShortTermProjects = createSelector(
  [selectProjectsState],
  (state) => state.showShortTermProjects
);

export const selectSelectedShortTermProjectsViewMode = createSelector(
  [selectProjectsState],
  (state) => state.selectedShortTermProjectsViewMode
);

export const selectMobileShowList = createSelector(
  [selectProjectsState],
  (state) => state.mobileShowList
);

export const selectProjectsIds = createSelector(
  [selectProjectsState],
  (state) => state.projects?.ids || []
);

export const selectSelectedProjectId = createSelector(
  [selectProjectsState],
  (state) => state.selectedProjectId
);

export const selectIsProjectsLoading = createSelector(
  [selectProjectsState],
  (state) => state.loading
);

export const selectUserProjectsLoaded = createSelector(
  [selectProjectsState],
  (state) => state.userProjectsLoaded
);

export const selectProjectsActivityFilterCacheKey = createSelector(
  [selectUserActiveEmployerInfo],
  (activeEmployerInfo) => getProjectsActivityFilterCacheKey(activeEmployerInfo)
);

// In the unsent filings list mode the view waits on that source alone: on its
// first load (pending, no ids cached yet) behind the full skeleton; on a
// refresh (pending over cached ids, as after a filing was sent) with the
// cached list on screen and the small "loading more" skeleton under it. A
// 'stale' source, to be asked again on the next load, is neither.
const getUnsentFilingsSourceLoading = (state, cacheKey) => {
  const isPending =
    state.verottajailmoitettavaProjectIdsRequestStatusByEmployer?.[cacheKey]?.[
      UNSENT_FILINGS_SOURCE_KEY
    ] === 'pending';
  const hasCachedIds = Array.isArray(
    state.verottajailmoitettavaProjectIdsByEmployer?.[cacheKey]?.[
      UNSENT_FILINGS_SOURCE_KEY
    ]
  );

  return {
    isFirstLoad: isPending && !hasCachedIds,
    isRefreshing: isPending && hasCachedIds,
  };
};

export const selectIsLoadingProjectsActivityFilter = createSelector(
  [
    selectProjectsState,
    selectProjectsActivityFilterMode,
    selectProjectsActivityFilterCacheKey,
    selectVerottajailmoitettavaListMode,
  ],
  (
    state,
    projectsActivityFilterMode,
    cacheKey,
    verottajailmoitettavaListMode
  ) => {
    if (
      !cacheKey ||
      projectsActivityFilterMode === PROJECTS_ACTIVITY_FILTER_MODES.ALL
    ) {
      return false;
    }

    if (
      projectsActivityFilterMode ===
      PROJECTS_ACTIVITY_FILTER_MODES.VEROTTAJAILMOITETTAVA
    ) {
      if (
        verottajailmoitettavaListMode ===
        VEROTTAJAILMOITETTAVA_LIST_MODES.UNSENT_FILINGS
      ) {
        return getUnsentFilingsSourceLoading(state, cacheKey).isFirstLoad;
      }

      const sourceStatuses =
        state.verottajailmoitettavaProjectIdsRequestStatusByEmployer?.[
          cacheKey
        ];

      if (!sourceStatuses) {
        return false;
      }

      const statuses = Object.values(sourceStatuses);

      // Hide skeleton as soon as any source returns — partial results render
      // immediately while slower sources keep loading in the background.
      if (statuses.some((status) => status === 'fulfilled')) {
        return false;
      }

      return statuses.some((status) => status === 'pending');
    }

    return (
      state.latestUserActiveProjectIdsRequestStatusByEmployer?.[cacheKey] ===
      'pending'
    );
  }
);

export const selectIsLoadingMoreVerottajailmoitettavaProjects = createSelector(
  [
    selectProjectsState,
    selectProjectsActivityFilterMode,
    selectProjectsActivityFilterCacheKey,
    selectVerottajailmoitettavaListMode,
  ],
  (
    state,
    projectsActivityFilterMode,
    cacheKey,
    verottajailmoitettavaListMode
  ) => {
    if (
      !cacheKey ||
      projectsActivityFilterMode !==
        PROJECTS_ACTIVITY_FILTER_MODES.VEROTTAJAILMOITETTAVA
    ) {
      return false;
    }

    if (
      verottajailmoitettavaListMode ===
      VEROTTAJAILMOITETTAVA_LIST_MODES.UNSENT_FILINGS
    ) {
      return getUnsentFilingsSourceLoading(state, cacheKey).isRefreshing;
    }

    const sourceStatuses =
      state.verottajailmoitettavaProjectIdsRequestStatusByEmployer?.[cacheKey];

    if (!sourceStatuses) {
      return false;
    }

    const statuses = Object.values(sourceStatuses);
    const anyFulfilled = statuses.some((status) => status === 'fulfilled');
    const anyPending = statuses.some((status) => status === 'pending');

    return anyFulfilled && anyPending;
  }
);

export const selectIsAddingEmployer = createSelector(
  [selectProjectsState],
  (state) => state.isAddingEmployer
);

export const selectProjectRecordHours = createSelector(
  [selectProjectsState],
  (state) => state.projectRecordHours
);

export const selectIsProjectRecordHoursLoading = createSelector(
  [selectProjectsState],
  (state) => state.projectRecordHoursLoading
);

export const selectLatestUserActiveProjectIds = createSelector(
  [selectProjectsState, selectProjectsActivityFilterCacheKey],
  (state, cacheKey) =>
    (cacheKey && state.latestUserActiveProjectIdsByEmployer?.[cacheKey]) || []
);

// Record-derived subset of the 30-day activity list (same fetch, second array).
export const selectLatestUserRecordProjectIds = createSelector(
  [selectProjectsState, selectProjectsActivityFilterCacheKey],
  (state, cacheKey) =>
    (cacheKey && state.latestUserRecordProjectIdsByEmployer?.[cacheKey]) || []
);

const selectVerottajailmoitettavaProjectIdsByEmployerMap = createSelector(
  [selectProjectsState],
  (state) => state.verottajailmoitettavaProjectIdsByEmployer
);

export const selectVerottajailmoitettavaProjectIds = createSelector(
  [
    selectVerottajailmoitettavaProjectIdsByEmployerMap,
    selectProjectsActivityFilterCacheKey,
    selectVerottajailmoitettavaListMode,
  ],
  (mapByEmployer, cacheKey, verottajailmoitettavaListMode) => {
    if (!cacheKey) {
      return [];
    }

    const sources = mapByEmployer?.[cacheKey];

    if (!sources) {
      return [];
    }

    // The unsent filings list mode keeps only that source's projects. Their
    // ancestors still come on top where the list adds them (the
    // ...WithAncestors selectors below, the tree in useProjectTreeData).
    const sourceProjectIdLists =
      verottajailmoitettavaListMode ===
      VEROTTAJAILMOITETTAVA_LIST_MODES.UNSENT_FILINGS
        ? [sources[UNSENT_FILINGS_SOURCE_KEY]]
        : Object.values(sources);

    return Array.from(
      new Set(
        sourceProjectIdLists.reduce(
          (acc, projectIds) => acc.concat(projectIds || []),
          []
        )
      )
    );
  }
);
export const selectSavingProcountorDimension = createSelector(
  [selectProjectsState],
  (state) => state.savingProcountorDimension
);

export const selectIsProjectCreateLoading = createSelector(
  [selectProjectsState],
  (state) => state.isProjectCreateLoading
);

export const selectUserProjectPreferences = createSelector(
  [selectProjectsState],
  (state) => state.userProjectPreferences || {}
);

export const selectUserProjectPreferencesLoaded = createSelector(
  [selectProjectsState],
  (state) => state.userProjectPreferencesLoaded
);

export const selectUserProjectPreferencesLoading = createSelector(
  [selectProjectsState],
  (state) => state.userProjectPreferencesLoading
);

export const selectShowHiddenProjects = createSelector(
  [selectProjectsState],
  (state) => Boolean(state.showHiddenProjects)
);

export const selectShowArchivedProjects = createSelector(
  [selectProjectsState],
  (state) => Boolean(state.showArchivedProjects)
);

export const selectShowEndedProjects = createSelector(
  [selectProjectsState],
  (state) => Boolean(state.showEndedProjects)
);

export const selectHasHiddenProjectPreferences = createSelector(
  [selectUserProjectPreferences],
  (preferences) =>
    Object.values(preferences).some((preference) => preference?.hidden)
);

export const selectProjectPreferenceByProject = (project) =>
  createSelector(
    [selectUserProjectPreferences],
    (preferences) => preferences[getUserProjectPreferenceKey(project)] || null
  );

export const selectIsProjectFavorite = (project) =>
  createSelector([selectProjectPreferenceByProject(project)], (preference) =>
    Boolean(preference?.favorite)
  );

export const selectIsProjectHidden = (project) =>
  createSelector([selectProjectPreferenceByProject(project)], (preference) =>
    Boolean(preference?.hidden)
  );

/**
 * How the project dashboard shows the project's sensor readings to the user,
 * SENSOR_DISPLAY.GAUGES or .DIGITAL: gauges until the user's preferences have
 * loaded, and without a choice.
 */
export const selectProjectSensorDisplay = (state, project) =>
  getSensorDisplayOfPreference(
    project
      ? selectUserProjectPreferences(state)[
          getUserProjectPreferenceKey(project)
        ]
      : null
  );

/**
 * The user's own dashboard layout for the project, as stored in their
 * project preference (`dashboardLayout`, `{version, items}`), or null: no
 * preferences loaded yet, no preference for the project, none saved, or
 * one cleared (`null`). Unresolved: the dashboard resolves it against the
 * module catalogue. A state without the projects slice answers null.
 */
export const selectProjectUserDashboardLayout = (state, project) => {
  if (!project) return null;
  const preference =
    state?.projects?.userProjectPreferences?.[
      getUserProjectPreferenceKey(project)
    ];
  const layout = preference?.dashboardLayout;
  return layout && typeof layout === 'object' && !Array.isArray(layout)
    ? layout
    : null;
};

export const selectBusinessIntegrationPartners = createSelector(
  [selectProjectsState],
  (state) => state.businessIntegrationPartners
);

export const selectIsUpdatingProjectStatus = createSelector(
  [selectProjectsState],
  (state) => state.isUpdatingProjectStatus
);

export const selectProjectActivityFromDate = createSelector(
  [selectProjectsState],
  (state) => state.projectActivityFromDate
);

export const selectProjectActivityToDate = createSelector(
  [selectProjectsState],
  (state) => state.projectActivityToDate
);

export const selectIsLoadingProjectActivity = createSelector(
  [selectProjectsState],
  (state) => state.isLoadingProjectActivity
);

export const selectProjectActivityData = createSelector(
  [selectProjectsState],
  (state) => state.projectActivityData
);

export const selectProjectFeatureStatusItems = createSelector(
  [selectProjectsState],
  (state) => state.projectFeatureStatusItems
);

export const selectSelectedStatusBasedProjectsMode = createSelector(
  [selectProjectsState],
  (state) => state.selectedStatusBasedProjectsMode
);

export const selectIsLoadingArchivedProjects = createSelector(
  [selectProjectsState],
  (state) => state.isLoadingArchivedProjects
);

export const selectProjectFeatureStatusItemsWithErrorStatus = createSelector(
  [selectProjectFeatureStatusItems],
  (statusItems) => statusItems.filter((item) => item.status === 'ERROR')
);

export const selectProjectFeatureErrorStatusItemsGroupedByProjectId =
  createSelector(
    [selectProjectFeatureStatusItemsWithErrorStatus],
    (projectStatusItems) => groupBy(projectStatusItems, 'projectId')
  );

export const selectSelectedProjectFeatureItemsWithErrorStatus = createSelector(
  [
    selectProjectFeatureErrorStatusItemsGroupedByProjectId,
    selectSelectedProjectId,
  ],
  (statusItems, selectedProjectId) =>
    statusItems[selectedProjectId] ? statusItems[selectedProjectId] : []
);

export const selectSelectedProjectEmployeeReportError = createSelector(
  [selectSelectedProjectFeatureItemsWithErrorStatus],
  (statusItems) =>
    statusItems.find((item) => item.feature === 'EMPLOYEE_REPORT')
);

export const selectProjectActivityProjectIds = createSelector(
  [selectProjectActivityData],
  (projectActivityData) => Object.keys(projectActivityData)
);

export const selectProjectsOfBusinessArray = createSelector(
  [selectProjectsOfBusiness],
  (projects) => Object.values(projects).filter((project) => !project.deletedAt)
);

export const selectAllProjectsOfBusiness = createSelector(
  [selectProjectsOfBusinessArray],
  (projects) => {
    return [...projects].sort((a, b) => {
      // Sort by name alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });
  }
);

export const selectProjectsArray = createSelector(
  [selectProjects],
  (projects) => Object.values(projects).filter((project) => !project.deletedAt)
);

export const selectNormalProjectsArray = createSelector(
  [selectProjectsArray],
  (projects) => projects.filter((project) => project.type === 'NORMAL')
);

export const selectShortTermProjects = createSelector(
  [selectProjectsArray],
  (projects) => projects.filter((project) => project.type === 'SHORT_TERM')
);

export const selectEmployerTypeProjects = createSelector(
  [selectProjectsArray],
  (projects) =>
    projects.filter((project) => project.businessTypeEnum === 'EMPLOYER')
);

export const selectProjectEmployeeEmployerProjects = createSelector(
  [selectProjectsState],
  (state) => state.projectEmployeeEmployerProjects.entities
);

export const selectProjectEmployeeEmployerProjectsArray = createSelector(
  [selectProjectEmployeeEmployerProjects],
  (employerProjects) => Object.values(employerProjects)
);

export const selectProjectsIndexedByParent = createSelector(
  [selectProjects],
  (projects) => groupBy(projects, 'parent')
);

export const selectChildProjectsOfSelectedProject = createSelector(
  [selectSelectedProjectId, selectProjectsIndexedByParent],
  (selectedProjectId, projectsIndexedByParent) =>
    projectsIndexedByParent[selectedProjectId]
      ? projectsIndexedByParent[selectedProjectId]
      : []
);

export const selectNormalProjectsArrayWithJoinedData = createSelector(
  [selectNormalProjectsArray, selectProjects],
  (projectsArray, projects) => {
    const joinedDataProjects = projectsArray.map((project) => ({
      ...project,
      masterParent: projects[project.masterParent]
        ? projects[project.masterParent]
        : { id: project.masterParent },
      parent: projects[project.parent]
        ? projects[project.parent]
        : { id: project.parent },
      employer: projects[project.employer]
        ? projects[project.employer]
        : { id: project.employer },
    }));
    return joinedDataProjects;
  }
);

export const selectProjectsArrayWithJoinedData = createSelector(
  [selectProjectsArray, selectProjects],
  (projectsArray, projects) => {
    const joinedDataProjects = projectsArray.map((project) => ({
      ...project,
      masterParent: projects[project.masterParent]
        ? projects[project.masterParent]
        : { id: project.masterParent },
      parent: projects[project.parent]
        ? projects[project.parent]
        : { id: project.parent },
      employer: projects[project.employer]
        ? projects[project.employer]
        : { id: project.employer },
    }));
    return joinedDataProjects;
  }
);

const expandProjectIdsWithAncestors = (projects, projectIds = []) => {
  const projectsById = arrayToMap(projects);
  const expandedIds = new Set(projectIds);

  projectIds.forEach((projectId) => {
    let currentProject = projectsById[projectId];

    while (currentProject?.parent?.id) {
      const parentId = currentProject.parent.id;

      if (expandedIds.has(parentId)) {
        break;
      }

      expandedIds.add(parentId);
      currentProject = projectsById[parentId];
    }
  });

  return [...expandedIds];
};

const appendEmployerProjects = (projects = []) =>
  Object.values(
    arrayToMap([
      ...projects,
      ...projects
        .filter((project) => project.masterParent?.id)
        .map((project) => project.masterParent),
    ])
  );

export const selectLatestUserActiveProjectIdsWithAncestors = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectLatestUserActiveProjectIds,
    selectUserProjectPreferences,
    selectProjects,
  ],
  (projects, latestActiveProjectIds, userProjectPreferences, projectsById) =>
    expandProjectIdsWithAncestors(
      projects,
      latestActiveProjectIds.filter((projectId) => {
        const project = projectsById[projectId];

        return (
          project &&
          !isProjectHiddenByPreferenceOrAncestor(
            project,
            userProjectPreferences,
            projectsById
          )
        );
      })
    )
);

// export const selectedProjectsArraySortedByName = createSelector(
//   [selectProjectsArrayWithJoinedData],
//   (projects) => {
//     return [...projects].sort((a, b) => {
//       return (a.name || '').localeCompare(b.name || '');
//     });
//   });

export const selectLatestUserActiveProjects = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectLatestUserActiveProjectIdsWithAncestors,
  ],
  (projects, latestActiveProjectIds) =>
    projects.filter((project) => latestActiveProjectIds.includes(project.id))
);

export const selectLatestUserActiveProjectsWithEmployers = createSelector(
  [selectLatestUserActiveProjects],
  (latestProjects) => appendEmployerProjects(latestProjects)
);

export const selectLatestUserActiveProjectsAndEmployerProjectsIds =
  createSelector(
    [selectLatestUserActiveProjectsWithEmployers],
    (latestProjects) => latestProjects.map((project) => project.id)
  );

export const selectLatestUserActiveProjectsAndEmployerProjectsIdsSet =
  createSelector(
    [selectLatestUserActiveProjectsWithEmployers],
    (latestProjects) => new Set(latestProjects.map((project) => project.id))
  );

// RECORDS source mode of the Projects list: same shape as the any-activity
// chain above, but built from the record-derived id subset only.
export const selectLatestUserRecordProjectIdsWithAncestors = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectLatestUserRecordProjectIds,
    selectUserProjectPreferences,
    selectProjects,
  ],
  (projects, recordProjectIds, userProjectPreferences, projectsById) =>
    expandProjectIdsWithAncestors(
      projects,
      recordProjectIds.filter((projectId) => {
        const project = projectsById[projectId];

        return (
          project &&
          !isProjectHiddenByPreferenceOrAncestor(
            project,
            userProjectPreferences,
            projectsById
          )
        );
      })
    )
);

export const selectLatestUserRecordProjects = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectLatestUserRecordProjectIdsWithAncestors,
  ],
  (projects, recordProjectIds) =>
    projects.filter((project) => recordProjectIds.includes(project.id))
);

export const selectLatestUserRecordProjectsWithEmployers = createSelector(
  [selectLatestUserRecordProjects],
  (recordProjects) => appendEmployerProjects(recordProjects)
);

export const selectLatestUserRecordProjectsAndEmployerProjectsIdsSet =
  createSelector(
    [selectLatestUserRecordProjectsWithEmployers],
    (recordProjects) => new Set(recordProjects.map((project) => project.id))
  );

export const selectVerottajailmoitettavaProjectIdsWithAncestors =
  createSelector(
    [selectProjectsArrayWithJoinedData, selectVerottajailmoitettavaProjectIds],
    (projects, verottajailmoitettavaProjectIds) =>
      expandProjectIdsWithAncestors(projects, verottajailmoitettavaProjectIds)
  );

export const selectVerottajailmoitettavaProjects = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectVerottajailmoitettavaProjectIdsWithAncestors,
  ],
  (projects, verottajailmoitettavaProjectIds) =>
    projects.filter((project) =>
      verottajailmoitettavaProjectIds.includes(project.id)
    )
);

export const selectVerottajailmoitettavaProjectsWithEmployers = createSelector(
  [selectVerottajailmoitettavaProjects],
  (verottajailmoitettavaProjects) =>
    appendEmployerProjects(verottajailmoitettavaProjects)
);

export const selectVerottajailmoitettavaProjectsAndEmployerProjectsIdsSet =
  createSelector(
    [selectVerottajailmoitettavaProjectsWithEmployers],
    (verottajailmoitettavaProjects) =>
      new Set(verottajailmoitettavaProjects.map((project) => project.id))
  );

export const selectProjectsEntitiesWithJoinedData = createSelector(
  [selectProjectsArrayWithJoinedData],
  (projects) => arrayToMap(projects)
);

export const selectSelectedProject = createSelector(
  [selectProjects, selectSelectedProjectId],
  (projects, selectedProjectId) =>
    projects[selectedProjectId] ? projects[selectedProjectId] : {}
);

export const selectSelectedProjectEmployer = createSelector(
  [selectEmployerTypeProjects, selectSelectedProject],
  (employerProjects, selectedProject) =>
    employerProjects.find(
      (employerProject) =>
        employerProject.business_id === selectedProject.business_id
    )
);

export const selectNormalProjectsOfSelectedBusiness = createSelector(
  [selectNormalProjectsArray, selectSelectedProject],
  (projects, selectedProject) =>
    projects.filter(
      (project) => project.business_id === selectedProject.business_id
    )
);

export const selectProjectById = (projectId) =>
  createSelector([selectProjects], (projects) => projects[projectId] || {});

export const selectIsSelectedProjectEmployerProject = createSelector(
  [selectSelectedProject],
  (project) => {
    const isEmployerProject =
      project.businessTypeEnum && project.businessTypeEnum === 'EMPLOYER';

    return isEmployerProject;
  }
);

export const selectSelectedBusinessShortTermProjects = createSelector(
  [selectSelectedProject, selectShortTermProjects],
  (selectedProject, shortTermProjects) =>
    shortTermProjects.filter(
      (shortTermProject) =>
        shortTermProject.business_id === selectedProject.business_id
    )
);

export const selectSearchFilteredShortTermProjects = createSelector(
  [
    selectSelectedBusinessShortTermProjects,
    selectShortTermProjectsSearchFilters,
  ],
  (shortTermProjects, searchFilters) => {
    const filteredProjects = shortTermProjects.filter((shortTermProject) => {
      const matchingName = shortTermProject.name
        .toLowerCase()
        .includes(searchFilters['name'].toLowerCase());

      const customerName = shortTermProject.customerName || '';

      const matchingCustomer = customerName
        .toLowerCase()
        .includes(searchFilters['customer'].toLowerCase());

      const orderNumber = shortTermProject.orderNumber?.toString() || '';

      const matchingOrderNumber = orderNumber.includes(
        searchFilters['orderNumber']
      );

      const projectAddress = shortTermProject.street1 || '';

      const matchingAddress = projectAddress
        .toLowerCase()
        .includes(searchFilters['address']);

      if (
        matchingName &&
        matchingCustomer &&
        matchingOrderNumber &&
        matchingAddress
      ) {
        return true;
      } else {
        return false;
      }
    });

    return filteredProjects;
  }
);

export const selectSelectedProjectWithJoinedData = createSelector(
  [selectProjectsEntitiesWithJoinedData, selectSelectedProjectId],
  (projects, selectedProjectId) =>
    projects[selectedProjectId] ? projects[selectedProjectId] : {}
);

export const selectSelectedProjectParent = createSelector(
  [selectProjects, selectSelectedProject],
  (projects, selectedProject) =>
    projects[selectedProject.parent] ? projects[selectedProject.parent] : {}
);

export const selectSelectedProjectMasterParent = createSelector(
  [selectProjects, selectSelectedProject],
  (projects, selectedProject) =>
    projects[selectedProject.masterParent]
      ? projects[selectedProject.masterParent]
      : {}
);

export const selectSelectedProjectRecordHourTotals = createSelector(
  [selectProjectRecordHours, selectSelectedProjectId],
  (projectRecordHours, selectedProjectId) =>
    projectRecordHours[selectedProjectId]
      ? projectRecordHours[selectedProjectId]
      : {}
);

export const selectProcountorDimensions = createSelector(
  [selectProjectsState],
  (projectsState) => projectsState.procountorDimensions
);

export const selectProjectMergeStatus = createSelector(
  [selectProjectsState],
  (state) => state.projectMergeStatus
);

export const selectProjectsMerging = createSelector(
  [selectProjectsState],
  (state) => state.projectsMerging
);

export const selectAddingProjectToMerge = createSelector(
  [selectProjectsState],
  (state) => state.addingProjectToMerge
);

export const selectProjectByKey = createSelector(
  [selectProjectsArray, (_, projectKey) => projectKey],
  (projects, projectKey) =>
    projects.find((project) => project.projectKey === projectKey)
);

export const selectSelectedProjectIntegrationPartners = createSelector(
  [selectSelectedProject, selectBusinessIntegrationPartners],
  (selectedProject, businessIntegrationPartners) => {
    return businessIntegrationPartners[selectedProject.business_id]
      ? businessIntegrationPartners[selectedProject.business_id]
      : [];
  }
);

export const selectSelectedEmployerBusinessNormalProjects = createSelector(
  [selectProjectsArray, selectSelectedProject],
  (projects, selectedProject) => {
    return projects.filter(
      (project) =>
        project.business_id === selectedProject.business_id &&
        project.type !== 'SHORT_TERM'
    );
  }
);

export const selectIsShadowProject = (projectId) =>
  createSelector(
    [selectProjectById(projectId), selectUserActiveEmployerBusinessId],
    (project, activeEmployerBusinessId) => {
      if (!project?.business_id || !activeEmployerBusinessId) {
        return false;
      }
      return project.business_id !== activeEmployerBusinessId;
    }
  );

export const selectProjectEditDialogTab = createSelector(
  [selectProjectsState],
  (state) => state.projectEditDialogTab
);
